import { createFileRoute } from '@tanstack/react-router';
import { useEffect, useState, useMemo } from 'react';
import {
  analyticsApi,
  skillApi,
  type OverviewStats,
  type VolumeStat,
  type CostStat,
  type SkillStat,
  type LatencyStats,
  type HandoffFunnel,
  type Skill
} from '@/lib/api';
import {
  Calendar,
  MessageSquare,
  Clock,
  UserCheck,
  RefreshCw,
  Cpu,
  BarChart3
} from 'lucide-react';
import styles from './_auth.analytics.module.css';

export const Route = createFileRoute('/_auth/analytics')({
  component: AnalyticsPage,
});

export default function AnalyticsPage() {
  const [range, setRange] = useState<string>('7d');
  const [loading, setLoading] = useState<boolean>(true);

  // States
  const [overview, setOverview] = useState<OverviewStats | null>(null);
  const [volumes, setVolumes] = useState<VolumeStat[]>([]);
  const [costs, setCosts] = useState<CostStat[]>([]);
  const [skills, setSkills] = useState<SkillStat[]>([]);
  const [latency, setLatency] = useState<LatencyStats | null>(null);
  const [handoffs, setHandoffs] = useState<HandoffFunnel | null>(null);
  const [skillsMeta, setSkillsMeta] = useState<Skill[]>([]);

  const loadData = async () => {
    setLoading(true);
    try {
      const [overviewRes, volumesRes, costsRes, skillsRes, latencyRes, handoffsRes, skillsMetaRes] = await Promise.all([
        analyticsApi.getOverview(range).catch(() => null),
        analyticsApi.getVolume(range).catch(() => []),
        analyticsApi.getCosts(range).catch(() => []),
        analyticsApi.getSkills(range).catch(() => []),
        analyticsApi.getLatency(range).catch(() => ({ p50: 0, p95: 0 })),
        analyticsApi.getHandoffs(range).catch(() => ({ totalConversations: 0, handoffsCount: 0, handoffRate: 0 })),
        skillApi.list().catch(() => []),
      ]);

      setOverview(overviewRes);
      setVolumes(volumesRes);
      setCosts(costsRes);
      setSkills(skillsRes);
      setLatency(latencyRes);
      setHandoffs(handoffsRes);
      setSkillsMeta(skillsMetaRes);
    } catch (err) {
      console.error('Failed to fetch analytics:', err);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadData();
  }, [range]);

  // Aggregate channels for donut/bar chart
  const channelData = useMemo(() => {
    const counts: Record<string, number> = {};
    volumes.forEach(v => {
      counts[v.channel] = (counts[v.channel] || 0) + v.count;
    });
    const total = Object.values(counts).reduce((a, b) => a + b, 0);
    return Object.entries(counts).map(([name, value]) => ({
      name,
      value,
      percentage: total > 0 ? (value / total) * 100 : 0,
    })).sort((a, b) => b.value - a.value);
  }, [volumes]);

  // Aggregate volume by day for line chart
  const dailyVolumeData = useMemo(() => {
    const volumeByDay: Record<string, number> = {};
    volumes.forEach(v => {
      volumeByDay[v.day] = (volumeByDay[v.day] || 0) + v.count;
    });
    return Object.entries(volumeByDay)
      .sort(([a], [b]) => a.localeCompare(b))
      .map(([day, count]) => ({ day, count }));
  }, [volumes]);

  // Map skill usage with names
  const skillUsageList = useMemo(() => {
    const skillCounts: Record<string, number> = {};
    skills.forEach(s => {
      skillCounts[s.skillId] = (skillCounts[s.skillId] || 0) + s.count;
    });

    return Object.entries(skillCounts)
      .map(([skillId, count]) => {
        const meta = skillsMeta.find(sm => sm.id === skillId);
        return {
          id: skillId,
          name: meta?.name ?? `Skill: ${skillId.substring(0, 8)}...`,
          type: meta?.implementationType ?? 'PROMPT_ONLY',
          count,
        };
      })
      .sort((a, b) => b.count - a.count);
  }, [skills, skillsMeta]);

  // Sparkline coordinates helper
  const renderAreaChart = (data: number[], daysLabel: string[]) => {
    if (data.length === 0) return <div className={styles.noChartData}>No data available for range</div>;
    const width = 500;
    const height = 150;
    const padding = 20;

    const max = Math.max(...data, 1);
    const min = 0;
    const rangeVal = max - min;

    const points = data.map((val, index) => {
      const x = padding + (index / (data.length - 1 || 1)) * (width - padding * 2);
      const y = height - padding - ((val - min) / rangeVal) * (height - padding * 2);
      return `${x.toFixed(1)},${y.toFixed(1)}`;
    });

    const pathData = `M ${points.join(' L ')}`;
    const areaPathData = `${pathData} L ${(width - padding).toFixed(1)},${(height - padding).toFixed(1)} L ${padding.toFixed(1)},${(height - padding).toFixed(1)} Z`;

    return (
      <svg className={styles.chartSvg} viewBox={`0 0 ${width} ${height}`}>
        <defs>
          <linearGradient id="chartGrad" x1="0" y1="0" x2="0" y2="1">
            <stop offset="0%" stopColor="var(--color-primary)" stopOpacity="0.3" />
            <stop offset="100%" stopColor="var(--color-primary)" stopOpacity="0.0" />
          </linearGradient>
        </defs>
        {/* Horizontal Grid lines */}
        {[0, 0.5, 1].map((r, i) => {
          const y = padding + r * (height - padding * 2);
          return (
            <line
              key={i}
              x1={padding}
              y1={y}
              x2={width - padding}
              y2={y}
              stroke="var(--color-border)"
              strokeWidth="1"
              strokeDasharray="4,4"
            />
          );
        })}
        {/* Area */}
        <path d={areaPathData} fill="url(#chartGrad)" />
        {/* Line */}
        <path d={pathData} fill="none" stroke="var(--color-primary)" strokeWidth="2.5" strokeLinecap="round" />
        {/* Vertices */}
        {points.map((pt, i) => {
          const [x, y] = pt.split(',');
          return (
            <g key={i} className={styles.chartDotGroup}>
              <circle cx={x} cy={y} r="4" fill="var(--color-surface)" stroke="var(--color-primary)" strokeWidth="2" />
              <title>{`${daysLabel[i]}: ${data[i]}`}</title>
            </g>
          );
        })}
      </svg>
    );
  };

  const formattedVolumeDays = dailyVolumeData.map(d => d.day.split('-').slice(1).join('/'));
  const formattedVolumeCounts = dailyVolumeData.map(d => d.count);

  const formattedCostDays = costs.map(c => c.day.split('-').slice(1).join('/'));
  const formattedCostEstimates = costs.map(c => c.estimatedCost);

  return (
    <div className={styles.page}>
      {/* ── Page Header ─────────────────── */}
      <div className={styles.header}>
        <div>
          <h1 className={styles.title}><BarChart3 size={22} /> Analytics</h1>
          <p className={styles.subtitle}>Historical analytics, cost estimates, latencies, and skill rates</p>
        </div>
        <div className={styles.headerActions}>
          <div className={styles.pickerWrapper}>
            <Calendar size={14} className={styles.pickerIcon} />
            <select
              id="analytics-range-select"
              className={styles.select}
              value={range}
              onChange={e => setRange(e.target.value)}
            >
              <option value="7d">Last 7 days</option>
              <option value="30d">Last 30 days</option>
              <option value="90d">Last 90 days</option>
            </select>
          </div>
          <button className={styles.refreshBtn} onClick={loadData} disabled={loading}>
            <RefreshCw size={14} className={loading ? styles.spin : ''} />
          </button>
        </div>
      </div>

      {loading ? (
        <div className={styles.loadingGrid}>
          {[1, 2, 3, 4].map(i => <div key={i} className={`${styles.statsCard} ${styles.skeleton}`} />)}
        </div>
      ) : (
        <>
          {/* ── Analytics Stats Grid ────────── */}
          <div className={styles.statsGrid}>
            <div className={styles.statsCard}>
              <div className={styles.cardHeader}>
                <MessageSquare size={16} className={styles.cardIconPrimary} />
                <span className={styles.cardTitle}>Total Volume</span>
              </div>
              <p className={styles.cardValue}>{overview?.totalConversations ?? 0}</p>
              <p className={styles.cardSubtitle}>{overview?.totalMessages ?? 0} total messages</p>
            </div>

            <div className={styles.statsCard}>
              <div className={styles.cardHeader}>
                <Clock size={16} className={styles.cardIconCyan} />
                <span className={styles.cardTitle}>Average Session</span>
              </div>
              <p className={styles.cardValue}>
                {overview?.avgDurationSeconds ? `${Math.round(overview.avgDurationSeconds / 60)} min` : '—'}
              </p>
              <p className={styles.cardSubtitle}>Avg turn count: {overview?.avgTurns ?? 0} turns</p>
            </div>

            <div className={styles.statsCard}>
              <div className={styles.cardHeader}>
                <Cpu size={16} className={styles.cardIconWarning} />
                <span className={styles.cardTitle}>Latency Percentiles</span>
              </div>
              <p className={styles.cardValue}>
                {latency?.p50 ? `${(latency.p50 / 1000).toFixed(2)}s` : '—'}
              </p>
              <p className={styles.cardSubtitle}>p95 latency: {latency?.p95 ? `${(latency.p95 / 1000).toFixed(2)}s` : '—'}</p>
            </div>

            <div className={styles.statsCard}>
              <div className={styles.cardHeader}>
                <UserCheck size={16} className={styles.cardIconSuccess} />
                <span className={styles.cardTitle}>Resolution Rate</span>
              </div>
              <p className={styles.cardValue}>
                {overview?.resolutionRate ? `${(overview.resolutionRate * 100).toFixed(0)}%` : '0%'}
              </p>
              <p className={styles.cardSubtitle}>
                Handoff Rate: {handoffs?.handoffRate ? `${(handoffs.handoffRate * 100).toFixed(1)}%` : '0.0%'}
              </p>
            </div>
          </div>

          {/* ── Charts Section ──────────────── */}
          <div className={styles.chartsGrid}>
            <div className={styles.chartBlock}>
              <h3 className={styles.chartBlockTitle}>Conversation Volume History</h3>
              <div className={styles.chartCanvas}>
                {renderAreaChart(formattedVolumeCounts, formattedVolumeDays)}
              </div>
              <p className={styles.chartFootnote}>Daily total chats processed across all active agents</p>
            </div>

            <div className={styles.chartBlock}>
              <h3 className={styles.chartBlockTitle}>Estimated Token Costs (USD)</h3>
              <div className={styles.chartCanvas}>
                {renderAreaChart(formattedCostEstimates, formattedCostDays)}
              </div>
              <p className={styles.chartFootnote}>Estimated platform cost based on tokens consumed</p>
            </div>
          </div>

          {/* ── Detailed Tables Section ────── */}
          <div className={styles.detailsGrid}>
            {/* Skills Usage */}
            <div className={styles.detailsBlock}>
              <h3 className={styles.detailsBlockTitle}>Executed Skills Execution Stats</h3>
              {skillUsageList.length === 0 ? (
                <div className={styles.emptyTable}>No skill executions recorded in this timeframe</div>
              ) : (
                <div className={styles.tableWrapper}>
                  <table className={styles.analyticsTable}>
                    <thead>
                      <tr>
                        <th>Skill Name</th>
                        <th>Type</th>
                        <th style={{ textAlign: 'right' }}>Executions</th>
                      </tr>
                    </thead>
                    <tbody>
                      {skillUsageList.map(skill => (
                        <tr key={skill.id}>
                          <td className={styles.skillNameCell}>{skill.name}</td>
                          <td>
                            <span className={`${styles.badge} ${styles[`badge_${skill.type.toLowerCase()}`]}`}>
                              {skill.type}
                            </span>
                          </td>
                          <td style={{ textAlign: 'right', fontWeight: 600 }}>{skill.count}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
            </div>

            {/* Channels Distribution */}
            <div className={styles.detailsBlock}>
              <h3 className={styles.detailsBlockTitle}>Channels Breakdown</h3>
              {channelData.length === 0 ? (
                <div className={styles.emptyTable}>No channel traffic recorded in this timeframe</div>
              ) : (
                <div className={styles.tableWrapper}>
                  <table className={styles.analyticsTable}>
                    <thead>
                      <tr>
                        <th>Channel Name</th>
                        <th style={{ textAlign: 'right' }}>Conversations</th>
                        <th style={{ textAlign: 'right' }}>Percentage</th>
                      </tr>
                    </thead>
                    <tbody>
                      {channelData.map(ch => (
                        <tr key={ch.name}>
                          <td style={{ fontWeight: 600 }}>{ch.name.replace('_', ' ')}</td>
                          <td style={{ textAlign: 'right' }}>{ch.value}</td>
                          <td style={{ textAlign: 'right', fontWeight: 600, color: 'var(--color-primary)' }}>
                            {ch.percentage.toFixed(1)}%
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
            </div>
          </div>
        </>
      )}
    </div>
  );
}
